<template>
    <div class="blog-list">
        <div class="blog-list__search" data-float-labels data-float-labels-class-prefix="form-field">
            <div class="row">
                <div class="col-12 col-lg">
                    <div class="form-field">
                        <input v-model="searchValue" id="search" type="text" autocomplete="off" @input="onSearchInput" />
                        <label for="search">Search</label>
                    </div>
                </div>
				<div class="col-12 col-lg-3 mt-2 mt-lg-0">
                    <div class="form-field">
                        <select id="category" v-model="selectedCategory" data-float-label>
                            <option value=""></option>
                            <option v-for="option of availableCategories" :key="`category-${option.id}`" :value="option.key">{{ option.name }}</option>
                        </select>
                        <label for="category">Category</label>
                    </div>
				</div>
            </div>
        </div>
        <div class="blog-list__body">
            <div class="row justify-content-center">
                <div class="col-12">
                    <div v-if="items.length > 0" :class="['blog-card-list', `blog-card-list--${theme}`]">
                        <a v-for="item of items" :key="`blog-item-${item.id}`" :class="['blog-card', `blog-card--${theme}`]" :href="item.url" :title="`Read about ${item.name }`">
                            <picture v-if="item.image" class="blog-card__image">
                                <img :src="item.image.url" :alt="item.image.altText" loading="lazy">
                            </picture>
                            <span class="blog-card__gradient"></span>
                            <div class="blog-card__content">
                                <p v-if="item.articleDate" class="blog-card__subtitle">
                                    {{ item.articleDate }}
                                </p>
                                <h3 class="blog-card__title title title--3">
                                    {{ item.name }}
                                </h3>
                                <div v-if="item.category && item.category.length > 0" class="badge-list justify-content-center mt-1">
                                    <div v-for="cat of item.category" class="badge badge--alt">
                                        {{ cat }}
                                    </div>
                                </div>
                            </div>
                            <span class="blog-card__arrow">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                                    <path d="M443.3 267.3c6.2-6.2 6.2-16.4 0-22.6l-176-176c-6.2-6.2-16.4-6.2-22.6 0s-6.2 16.4 0 22.6L393.4 240 16 240c-8.8 0-16 7.2-16 16s7.2 16 16 16l377.4 0L244.7 420.7c-6.2 6.2-6.2 16.4 0 22.6s16.4 6.2 22.6 0l176-176z"/>
                                </svg>
                            </span>
                        </a>
                    </div>
                    <div v-else class="blog-list__no-results richtext">
                        <p>Your search and/or selected category returned no results, please try again.</p>
                     </div>
                </div>
                <div v-if="items.length > 0 && currentPage < totalPages" class="col-auto">
                    <button class="button button--primary mt-4" @click="searchBlog">Load more</button>
                </div>
            </div>
        </div>
        <div v-if="isLoading" class="site-loader">
            <div class="sk-cube-grid">
                <div class="sk-cube sk-cube1"></div>
                <div class="sk-cube sk-cube2"></div>
                <div class="sk-cube sk-cube3"></div>
                <div class="sk-cube sk-cube4"></div>
                <div class="sk-cube sk-cube5"></div>
                <div class="sk-cube sk-cube6"></div>
                <div class="sk-cube sk-cube7"></div>
                <div class="sk-cube sk-cube8"></div>
                <div class="sk-cube sk-cube9"></div>
            </div>
        </div>
    </div>
</template>
<script>
const GetHttp = () => import(/* webpackChunkName: 'http' */ '../../_http').then(v=>v.default);
import { floatLabels } from '../../partials/_float-labels';

export default {
    data: () => ({
        searchValue: '',
        activeSearchValue: '',
        totalPages: 1,
        parentId: 0,
        currentPage: 1,
        selectedCategory: '',
        items: [],
        availableCategories: [],
        isLoading: false,
        inputTimeout: null,
        http: null
    }),
    props: {
        initialItems: {
            type: Array,
            required: true,
            default: () => []
        },
        initialTotalPages: {
            type: Number,
            required: true,
            default: 1
        },
        categories: {
            type: Array,
            required: true,
            default: () => []
        },
        parent: {
            type: Number,
            required: true,
            default: 0
        },
        theme: {
            type: String,
            default: "primary"
        }
    },
    watch: {
        selectedCategory: {
            handler: function(newVal,oldVal) {
                if(this.isLoading || newVal === oldVal) return;

                this.$nextTick(() => {
                    this.currentPage = 0;
                    this.searchBlog();
                })
            }
        }
    },
    methods: {
        onSearchInput() {
            if(this.isLoading) {
                // Reassign the last stored value until the search request resolves
                this.searchValue = this.activeSearchValue;
                return false;
            }
            clearTimeout(this.inputTimeout);
            this.inputTimeout = setTimeout(() => {
                this.currentPage = 0;
                this.selectedCategories = [];
                this.searchBlog();
            },500);
        },
        clearCategory() {
            if(this.isLoading) return;

            this.selectedCategory = '';
            this.currentPage = 0;
            this.searchBlog();
        },
        async searchBlog() {
            this.isLoading = true;

            this.activeSearchValue = `${this.searchValue}`;

            if(!this.http) {
                this.http = await GetHttp();
            }

            try {
                this.http.get('/search/blogsearch', {
                    params: {
                        query: this.activeSearchValue || '',
                        categories: this.selectedCategory,
                        page: this.currentPage + 1,
                        parentId: this.parentId
                    }
                }).then(({ data }) => {
                    if (this.currentPage === 0) {
                        this.items = data.items;
                    } else {
                        this.items = [...this.items, ...data.items];
                    }
                    this.totalPages = data.totalPages;

                    this.currentPage++;
                    this.isLoading = false;
                });
            } catch (error) {
                // Handle the error here, e.g., log it or show a user-friendly message.
                console.error("An error occurred during the HTTP request:", error);
            }
        }
    },
    mounted() {
        this.totalPages = this.initialTotalPages;
        this.items = this.initialItems;
        this.availableCategories = this.categories;
        this.parentId = this.parent;
    },
    updated() {
        floatLabels();
    }
}
</script>